import { createApiClient } from './client'

export interface Notification {
  id: string
  userId: string
  type: string
  title: string
  message: string
  status: 'unread' | 'read' | 'archived'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  metadata?: any
  relatedEntityId?: string
  relatedEntityType?: string
  actionUrl?: string
  actionText?: string
  createdAt: string
  readAt?: string
  expiresAt?: string
}

export interface NotificationListResponse {
  notifications: Notification[]
  total: number
  unreadCount: number
  hasMore: boolean
  limit: number
  offset: number
}

export interface NotificationStatistics {
  total: number
  unread: number
  read: number
  archived: number
  byType: Record<string, number>
  byPriority: Record<string, number>
}

// Notifications API class
export class NotificationsApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  async getNotifications(params?: {
    status?: 'unread' | 'read' | 'archived'
    type?: string
    priority?: 'low' | 'medium' | 'high' | 'urgent'
    limit?: number
    offset?: number
    sortBy?: 'created_at' | 'read_at' | 'priority'
    sortOrder?: 'asc' | 'desc'
    unreadOnly?: boolean
    tripId?: string
  }): Promise<NotificationListResponse> {
    const response = await this.apiClient.get<{ success: boolean; data: NotificationListResponse }>('/notifications', params)
    return response.data
  }

  async getUnreadCount(): Promise<number> {
    const response = await this.apiClient.get<{ success: boolean; data: { count: number } }>('/notifications/count/unread')
    return response.data.count
  }

  async getNotification(notificationId: string): Promise<Notification> {
    const response = await this.apiClient.get<{ success: boolean; data: Notification }>(`/notifications/${notificationId}`)
    return response.data
  }

  async markAsRead(notificationId: string): Promise<Notification> {
    const response = await this.apiClient.put<{ success: boolean; data: Notification }>(`/notifications/${notificationId}`, {
      status: 'read'
    })
    return response.data
  }

  async markManyAsRead(notificationIds: string[]): Promise<{ markedCount: number }> {
    const response = await this.apiClient.post<{ success: boolean; data: { markedCount: number } }>('/notifications/mark-read', {
      notificationIds
    })
    return response.data
  }

  async markAllAsRead(): Promise<{ markedCount: number }> {
    const response = await this.apiClient.post<{ success: boolean; data: { markedCount: number } }>('/notifications/mark-all-read')
    return response.data
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await this.apiClient.delete(`/notifications/${notificationId}`)
  }

  async deleteAllNotifications(): Promise<{ deletedCount: number }> {
    const response = await this.apiClient.delete<{ success: boolean; data: { deletedCount: number } }>('/notifications/user')
    return response.data
  }

  async getByType(type: string, params?: {
    limit?: number
    offset?: number
  }): Promise<NotificationListResponse> {
    const response = await this.apiClient.get<{ success: boolean; data: NotificationListResponse }>(`/notifications/type/${type}`, params)
    return response.data
  }

  async getByPriority(priority: 'low' | 'medium' | 'high' | 'urgent', params?: {
    limit?: number
    offset?: number
  }): Promise<NotificationListResponse> {
    const response = await this.apiClient.get<{ success: boolean; data: NotificationListResponse }>('/notifications', {
      priority,
      ...params
    })
    return response.data
  }

  async archiveNotifications(notificationIds: string[]): Promise<{ archivedCount: number }> {
    const response = await this.apiClient.post<{ success: boolean; data: { archivedCount: number } }>('/notifications/archive', {
      notificationIds
    })
    return response.data
  }
}
